import React, { useState, useEffect } from 'react';
import { Link } from 'gatsby';
import { CloseIcon, HamburgerIcon } from './menu-icon';
import MenuModal from './menu-modal';
import StrapiImage from '../util/StrapiImage';
import { useStrapiContext } from '../context/StrapiContext';
import { FaPhone } from 'react-icons/fa';

function formatPhoneNumber(phoneNumber) {
  const cleaned = ('' + phoneNumber).replace(/\D/g, '');
  const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
  if (match) {
    const formattedNumber = `(${match[1]}) ${match[2]} ${match[3]}`;
    return (
      <a
        href={`tel:+1${phoneNumber}`}
        className="unstyled-link"
      >{`${formattedNumber} `}</a>
    );
  }
  return null;
}

// Define navigation links
const links = [
  { name: 'About', url: '/about-us', dropdown: [
    { name: 'Our Team', url: '/about-us' },
    { name: 'Areas We Serve', url: '/services/service-area' },
  ] },
  {
    name: 'Services',
    url: '/services',
    dropdown: [
      { name: 'Replacement Windows', url: '/services/replacement-windows' },
      { name: 'Patio Doors', url: '/services/patio-doors' },
      { name: 'Entry Doors', url: '/services/entry-doors' },
      { name: 'Pet Doors', url: '/services/pet-doors' },
      { name: 'Warranty', url: '/services/warranty' },
    ],
  },
  { name: 'Financing & Promotions', url: '/offers-and-promotions' },
  { name: 'Gallery', url: '/gallery' },
  { name: 'Blog', url: '/blog' },
  { name: 'Contact', url: '/contact' },
];

export default function Header({ location, headerRef }) {
  const strapi = useStrapiContext();
  const logo =
    strapi.strapiGlobal.logo.localFile.childImageSharp.gatsbyImageData;
  const [isMobileMenuOpen, toggleMobileMenu] = useState(false);
  const [isVisible, setIsVisible] = useState(location.pathname !== '/');

  useEffect(() => {
    if (location.pathname === '/') {
      // Handle scroll event to toggle visibility of header on the home page
      const handleScroll = () => {
        const shouldShow = window.scrollY > window.innerHeight * 0.4;
        setIsVisible(shouldShow);
      };
      window.addEventListener('scroll', handleScroll);
      return () => window.removeEventListener('scroll', handleScroll);
    }
  }, [location.pathname]);

  const handleClick = () => {
    toggleMobileMenu(!isMobileMenuOpen);
  };

  //const adRef = location && location.search === '?ad_ref';

  return (
    <>
      <header
        ref={headerRef}
        className={`drop-shadow-md fixed top-0 left-0 right-0 transition-opacity duration-300 ease-in-out ${
          isVisible
            ? 'opacity-100 pointer-events-auto'
            : 'opacity-0 pointer-events-none'
        }`}
        style={{
          transform: isVisible ? 'translateY(0)' : 'translateY(-100%)',
          willChange: 'transform',
          zIndex: 998,
        }}
      >
        {/* Contact information bar */}
        <div className="flex flex-row items-end justify-end border-brandDark bg-brandDark px-2 py-0">
          <div className="mt-0 flex flex-col md:flex-row items-center justify-center">
            <p className="hidden my-0 md:block text-sm md:text-lg font-bold text-white text-center md:text-left">
              <FaPhone className="inline-block mr-2 my-2" role="img" aria-label="DFW Phone number" /> DFW:{' '}
              {formatPhoneNumber(9723933991)}
              <FaPhone className="inline-block ml-4 my-2 mr-2" role="img" aria-label="TEXOMA Phone number" /> TEXOMA:{' '}
              {formatPhoneNumber(9034517577)}
            </p>
          </div>
        </div>
        {/* Main navigation bar */}
        <nav className="border-gray-200 bg-white px-4 py-6 lg:px-6">
          <div className="mx-auto flex flex-wrap items-center justify-between">
            {/* Logo */}
            <Link to="/">
              <StrapiImage
                img={logo}
                className="my-2 w-[250px] transform transition-transform hover:scale-110"
              />
            </Link>

            {/* Get Free Estimate button and menu toggle for mobile */}
            <div className="flex items-center lg:order-2">
              {location.pathname !== '/get-quote/' && (
                <div className="hidden md:flex flex-col gap-2">
                  <Link
                    to="/get-quote"
                    className="uppercase transform rounded-sm border-2 border-brandLight bg-transparent px-4 py-2 text-center text-xl font-bold text-brandLight shadow transition-transform hover:scale-105 hover:border-transparent hover:bg-brandLight hover:text-white hover:shadow-lg"
                  >
                    Get Your Free Estimate
                  </Link>
                  {/* <Link
                    to="/nominate"
                    className="uppercase transform rounded-sm border-2 border-gray-600 bg-gray-100/10 px-4 py-2 text-center text-lg font-bold text-gray-600 shadow transition-transform hover:scale-105 hover:border-gray-700 hover:bg-gray-200 hover:text-gray-700 hover:shadow-lg"
                  >
                    Nominate a Family
                  </Link> */}
                </div>
              )}
              <button
                id="modal-menu"
                data-collapse-toggle="mobile-menu-2"
                type="button"
                className="mx-5 inline-flex items-center rounded-sm p-2 text-sm text-gray-900 hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-600 lg:hidden"
                aria-controls="mobile-menu-2"
                aria-expanded="false"
                onClick={handleClick}
              >
                {isMobileMenuOpen ? (
                  <CloseIcon color="#06283f" size="20px" />
                ) : (
                  <HamburgerIcon color="#06283f" size="24px" />
                )}
              </button>
            </div>

            {/* Navigation links */}
            <div
              className="hidden w-full items-center justify-between lg:order-1 lg:flex lg:w-auto"
              id="mobile-menu-2"
            >
              <ul className="mt-4 flex flex-col font-medium lg:mt-0 lg:flex-row lg:space-x-8">
                {links.map((link) => {
                  if (link.dropdown) {
                    return (
                      <li key={link.url} className="relative group">
                        <Link to={link.url}>
                          <button className="border-b my-4 lg:border-0 block transform border-gray-700 py-2 pl-3 pr-4 text-gray-900 transition-transform hover:scale-110 hover:text-gray-500 lg:p-0 lg:hover:bg-transparent">
                            {link.name}
                          </button>
                        </Link>
                        <ul className="absolute hidden group-hover:block bg-white shadow-lg mt-0 w-48">
                          {link.dropdown.map((dropdownLink) => (
                            <li key={dropdownLink.url}>
                              <Link
                                to={dropdownLink.url}
                                className="block px-4 py-4 text-sm text-gray-700 hover:bg-gray-100"
                              >
                                {dropdownLink.name}
                              </Link>
                            </li>
                          ))}
                        </ul>
                      </li>
                    );
                  }
                  return (
                    <li key={link.url}>
                      <Link
                        to={link.url}
                        className="border-b my-4 lg:border-0 block transform border-gray-700 py-2 pl-3 pr-4 text-gray-900 transition-transform hover:scale-110 hover:text-gray-500 lg:p-0 lg:hover:bg-transparent"
                      >
                        {link.name}
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>
        </nav>
      </header>

      {/* Mobile menu modal */}
      {isMobileMenuOpen && <MenuModal links={links} handleClick={handleClick}/>}
    </>
  );
}
